<template>
  <div v-if="serverData" class="col-12 col-lg-6 mb-4 p-2 p-md-3">
    <v-card class="analytics-card shadow rounded-md d-flex flex-column">
      <v-card-text class="pa-6 d-flex flex-column">
        <div class="d-flex justify-space-between align-start mb-6">
          <div class="flex-grow-1">
            <div class="d-flex align-center mb-2">
              <div class="icon-wrapper bg-light-info rounded mr-3">
                <v-icon color="info" size="24">mdi-cube-outline</v-icon>
              </div>
              <h3 class="font-weight-bold text-dark-75 mb-0">
                {{
                  serverData.title
                    ? serverData["title"]
                    : $t("customer_inventory_overview")
                }}
              </h3>
            </div>
            <p class="text-muted mb-0 ml-11 font-size-sm">
              {{
                $t(
                  "real_time_inventory_distribution_and_stock_levels_per_customer_account"
                )
              }}
            </p>
          </div>
          <v-btn
            icon
            small
            color="info"
            class="elevation-0"
            @click="exportChart(serverData.print_url)"
          >
            <v-icon size="20">mdi-download</v-icon>
          </v-btn>
        </div>

        <div class="summary-section pt-6">
          <v-row class="ma-0 summary-grid" dense>
            <v-col cols="12" sm="6" class="pa-2 pa-sm-3">
              <div class="summary-card-gradient text-center py-4 rounded">
                <div class="d-flex align-center justify-center mb-3">
                  <v-icon size="20" class="mr-2" style="color: #7322b0"
                    >mdi-account-group</v-icon
                  >
                  <p class="font-size-sm font-weight-medium text-dark-50 mb-0">
                    {{ $t("number_of_customers") }}
                  </p>
                </div>
                <div class="stat-badge-primary">
                  <h2 class="font-weight-bold mb-0" style="color: #7322b0">
                    {{ serverData.total_customers }}
                  </h2>
                </div>
              </div>
            </v-col>
            <v-col cols="12" sm="6" class="pa-2 pa-sm-3">
              <div class="summary-card-gradient text-center py-4 rounded">
                <div class="d-flex align-center justify-center mb-3">
                  <v-icon size="20" class="mr-2" style="color: #c32c80"
                    >mdi-cube-outline</v-icon
                  >
                  <p class="font-size-sm font-weight-medium text-dark-50 mb-0">
                    {{ $t("total_inventory") }}
                  </p>
                </div>
                <div class="stat-badge-secondary">
                  <h2 class="font-weight-bold mb-0" style="color: #c32c80">
                    {{ serverData.total }}
                  </h2>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>

        <div
          class="table-container overflow-auto adaptive-table-height d-none d-md-block"
        >
          <v-simple-table class="modern-table">
            <template v-slot:default>
              <thead>
                <tr class="table-header">
                  <th class="text-left font-weight-bold text-dark-75">
                    {{ $t("customer") }}
                  </th>
                  <th class="text-right font-weight-bold text-dark-75">
                    {{ $t("total_inventory") }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, i) in serverData.values"
                  :key="i"
                  class="table-row"
                  :class="{ 'row-hover': true }"
                >
                  <td
                    class="font-size-base font-weight-medium text-dark-75 py-3"
                  >
                    <client-column :value="item.customer">{{
                      item.customer
                    }}</client-column>
                  </td>
                  <td class="text-right py-3">
                    <span class="inventory-count-simple">
                      {{ item.total }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
        </div>

        <div class="mobile-card-list d-md-none">
          <div
            v-for="(item, i) in serverData.values"
            :key="'mobile-' + i"
            class="mobile-row"
          >
            <div class="mobile-row-customer">
              <client-column :value="item.customer" />
            </div>
            <div class="mobile-row-meta">
              <span class="inventory-count-simple">{{ item.total }}</span>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
  <div class="col-12 col-lg-6 mb-4" v-else>
    <v-skeleton-loader type="card"></v-skeleton-loader>
  </div>
</template>

<script>
import ApiService from "@/core/services/api.service";
import ClientColumn from "@/own/components/datatable/ClientColumn.vue";

export default {
  name: "AnalyticsInventory",
  props: ["item", "filterData", "exportChart"],
  components: {
    ClientColumn,
  },
  data: () => ({
    serverData: null,
  }),
  beforeMount() {
    this.loadDataFromServer();
  },
  methods: {
    loadDataFromServer() {
      let data = this.filterData;
      ApiService.post(`${this.item.url}`, data)
        .then((response) => {
          this.serverData = response.data.chart;
        })
        .catch(() => {});
    },
  },
  computed: {
    isDataLoaded() {
      return !!this.serverData;
    },
  },
  watch: {
    filterData() {
      this.serverData = null;
      this.loadDataFromServer();
    },
  },
};
</script>

<style lang="scss">
.icon-wrapper {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bg-light-info {
  background-color: rgba(195, 44, 128, 0.1);
}

.text-dark-50 {
  color: rgba(0, 0, 0, 0.5);
}

.text-dark-75 {
  color: rgba(0, 0, 0, 0.75);
}

.text-muted {
  color: #6c757d;
}

.analytics-card .table-container {
  width: 100%;
  min-width: 0;
}

.analytics-card .modern-table {
  width: 100%;
  max-width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}

.analytics-card .modern-table th,
.analytics-card .modern-table td {
  white-space: normal;
  word-break: break-word;
}

.modern-table {
  thead tr.table-header {
    background-color: #f8f9fa;
    border-bottom: 2px solid #e9ecef;

    th {
      padding: 12px 16px;
      font-size: 0.875rem;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      border-bottom: none !important;
    }
  }

  tbody tr.table-row {
    border-bottom: 1px solid #f1f3f5;
    transition: all 0.2s ease;

    &:hover {
      background-color: #f8f9fa;
    }

    td {
      border-bottom: none !important;
    }
  }
}

.inventory-count-simple {
  display: inline-block;
  font-weight: 700;
  font-size: 1.1rem;
  color: #c32c80;
  padding: 4px 12px;
  background-color: rgba(195, 44, 128, 0.08);
  border-radius: 6px;
  transition: all 0.2s ease;

  &:hover {
    background-color: rgba(195, 44, 128, 0.15);
  }
}

.summary-section {
  border-top: 2px solid #f1f3f5;

  .summary-card-gradient {
    background: linear-gradient(
      135deg,
      rgba(115, 34, 176, 0.05) 0%,
      rgba(195, 44, 128, 0.05) 100%
    );
    border: 1.5px solid rgba(195, 44, 128, 0.15);
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(115, 34, 176, 0.1) 0%,
        rgba(195, 44, 128, 0.1) 100%
      );
      border-color: rgba(195, 44, 128, 0.3);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(195, 44, 128, 0.15);
    }
  }

  .stat-badge-primary {
    display: inline-block;
    padding: 8px 24px;
    background: linear-gradient(
      135deg,
      rgba(115, 34, 176, 0.15) 0%,
      rgba(115, 34, 176, 0.1) 100%
    );
    border: 1.5px solid rgba(115, 34, 176, 0.3);
    border-radius: 24px;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(115, 34, 176, 0.25) 0%,
        rgba(115, 34, 176, 0.2) 100%
      );
      border-color: rgba(115, 34, 176, 0.5);
      transform: scale(1.05);
      box-shadow: 0 2px 8px rgba(115, 34, 176, 0.2);
    }
  }

  .stat-badge-secondary {
    display: inline-block;
    padding: 8px 24px;
    background: linear-gradient(
      135deg,
      rgba(195, 44, 128, 0.15) 0%,
      rgba(195, 44, 128, 0.1) 100%
    );
    border: 1.5px solid rgba(195, 44, 128, 0.3);
    border-radius: 24px;
    transition: all 0.3s ease;

    &:hover {
      background: linear-gradient(
        135deg,
        rgba(195, 44, 128, 0.25) 0%,
        rgba(195, 44, 128, 0.2) 100%
      );
      border-color: rgba(195, 44, 128, 0.5);
      transform: scale(1.05);
      box-shadow: 0 2px 8px rgba(195, 44, 128, 0.2);
    }
  }
}

.analytics-card {
  min-height: auto;
}

.summary-grid .v-col {
  display: flex;
}

.adaptive-table-height {
  max-height: 35vh;

  @media (min-width: 1440px) {
    max-height: 400px;
  }

  @media (max-width: 767px) {
    max-height: none;
  }
}

.table-container {
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f3f5;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 4px;

    &:hover {
      background: #a0aec0;
    }
  }
}

.mobile-card-list {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.mobile-card-list .mobile-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  padding: 10px 4px;
  border-bottom: 1px solid #f1f3f5;
  min-width: 0;
}

.mobile-card-list .mobile-row-customer {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
}

.mobile-card-list .mobile-row-customer > div {
  display: flex !important;
  align-items: center;
  min-width: 0;
  width: 100%;
  padding-inline-start: 0 !important;
}

.mobile-card-list .mobile-row-customer .first-text {
  display: block !important;
  min-width: 0;
  flex: 1 1 auto;
  max-width: none !important;
}

.mobile-card-list .mobile-row-meta {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
}

@media (max-width: 767px) {
  .analytics-card .v-card-text {
    padding: 1rem !important;
  }

  .summary-section {
    padding-top: 1.25rem;
  }

  .summary-grid .v-col {
    padding-inline-start: 0 !important;
    padding-inline-end: 0 !important;
  }
}
</style>
